import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import Footer from "@buddieshr/components/footer";
import Header from "@buddieshr/components/header";
import MetaHeader from "@buddieshr/components/metaHeader";
import styles from "@buddieshr/styles/cost-of-turnover.module.scss";
import { Divider, Typography } from "antd";
import PageWrapper from "@buddieshr/components/templates/pageWrapper";
import TurnoverCostCalculatorPanel from "@buddieshr/components/turnnover/turnoverCostCalculatorPanel";
import Image from "next/image";
import Link from "next/link";
import TextBackground from "@buddieshr/components/text/textBackground";

const { Text } = Typography;

export default function CostTurnover({ refSource }) {
  const router = useRouter();
  const { t } = useTranslation();
  return (
    <>
      <MetaHeader
        title={t("metatitleCostTurnover")}
        description={t("descriptionCostTurnover")}
        currentURL={`https://buddieshr.com/${
          router.locale === "en" ? "" : router.locale
        }/cost-of-turnover`}
        currentShare="https://buddieshr.com/img/share_preview_1.png"
        pageSlug="cost-of-turnover"
        locale={router.locale}
      />
      <div>
        <Header />
        <main>
          <div className={styles.wrapper}>
            <div className={styles.inner}>
              <h1 className={styles.title}>
                The True Costs of{" "}
                <TextBackground>Employee Turnover</TextBackground>
              </h1>
              <div className={styles.subtitle}>
                Salaries and compensations are the largest expenses you have in
                a business. It is a recurring expense every month and is
                required to run the business. Calculating the cost of turnover
                and its impact on the bottom line is often forgotten or
                underestimated.
                <br />
                <br />
                Here is an advanced turnover cost calculator taking the full
                spectrum of impacts into account.
              </div>
              <Divider />
              <div>
                <PageWrapper>
                  <TurnoverCostCalculatorPanel />
                  <div className={styles.panel}>
                    <div>
                      <div style={{ margin: "auto", textAlign: "center" }}>
                        <Image
                          src="/img/resignation.jpg"
                          alt="resignation"
                          width={900}
                          height={600}
                          className={styles.pic}
                        />
                      </div>
                      <h2 className={styles.title}>
                        What is the cost of employee turnover?
                      </h2>

                      <div>
                        The cost of turnover is the cost associated with turning
                        over one position. It includes <b>direct costs</b> and
                        indirect costs.
                        <div className={styles.costsWrapper}>
                          <div className={styles.costs}>
                            <b>Direct costs</b>
                            <ul className={styles.list}>
                              <li>
                                Recruitment costs (job postings, advertising,
                                agency fees, background checks)
                              </li>
                              <li>Training and onboarding expenses</li>
                              <li>
                                Separation costs (severance pay, unused paid
                                time off)
                              </li>
                              <li>Exit interviews and administrative tasks</li>
                              <li>
                                Temporary staffing or overtime costs to cover
                                the vacant position
                              </li>
                              <li>
                                Costs of conducting interviews and assessments
                              </li>
                            </ul>
                          </div>
                          <div className={styles.costs}>
                            <b>Indirect costs</b>
                            <ul className={styles.list}>
                              <li>
                                decreased productivity during the transition
                                period Loss of institutional knowledge and
                                expertise
                              </li>
                              <li>Impact on employee morale and engagement</li>
                              <li>
                                Potential impact on customer relationships
                              </li>
                              <li>
                                Management and HR staff time spent on
                                recruitment and onboarding
                              </li>
                            </ul>
                          </div>
                        </div>
                        <br />
                        <br />
                        Turnover is inevitable for a company. Companies can have
                        a high turnover rate or a low one depending on their
                        retention rate which is mostly impacted by the company
                        culture, values and vision. Turnover may be a part of
                        business, but high turnover is largely indicative of
                        deeper issues.
                        <br />
                        <br />
                        Startup have a higher turnover rate by default because
                        of various factors like work cadence, less experienced
                        management, higher progression curve.
                      </div>
                    </div>
                  </div>
                  <div className={styles.panel}>
                    <h2 className={styles.title}>
                      How to Calculate Employee Turnover Rate?
                    </h2>
                    <div>
                      The calculation is based on the number of employees in the
                      company compared to the number of employees who actually
                      leave. You can compute the numbers to have a monthly or
                      yearly turnover rate.
                      <br />
                      <br />
                      <h3 className={styles.title}>
                        Formula to calculate turnover rate:
                      </h3>
                      <Text code copyable>
                        Turnover rate = [(# of employee separations) / (average
                        # of employees)] x 100
                      </Text>
                    </div>
                  </div>
                  <Divider />
                  <div className={styles.panel}>
                    <h2 className={styles.title}>
                      Why is it important to reduce employee turnover?
                    </h2>
                    <div>
                      A high employee turnover rate has a lot of consequences in
                      a company. It includes:
                      <ul className={styles.list}>
                        <li>Lost productivity</li>
                        <li>Lower employee morale</li>
                        <li>Weakened employer reputation</li>
                        <li>Even more turnover</li>
                        <li>Lowered Employee Engagement</li>
                        <li>Stagnation in Innovation</li>
                      </ul>
                      <div>
                        {
                          "That's why it is important to keep turnover rate as low as possible. It can have a significant impact on an organization's overall performance, culture, and bottom line."
                        }
                      </div>
                    </div>
                  </div>
                  <Divider />
                  <div className={styles.panel}>
                    <h2 className={styles.title}>
                      How to reduce employee turnover?
                    </h2>
                    <div>
                      Employee turnover is not a fatality. You can reduce it and
                      control it as long as you have the right method. Here are
                      6 ways to help reduce employee turnover:
                      <ul className={styles.list}>
                        <li className={styles.bold}>Recognize employees.</li>
                        <li className={styles.bold}>
                          Ask for feedback and act on it.
                        </li>
                        <li className={styles.bold}>
                          Promote your company values and make them actionable.
                        </li>
                        <li className={styles.bold}>
                          Share your company mission. Again and again.
                        </li>
                        <li className={styles.bold}>
                          Improve your benefits package.
                        </li>
                        <li className={styles.bold}>
                          Conduct exit interviews.
                        </li>
                        <li>
                          Article:{" "}
                          <Link
                            target="_blank"
                            href="https://blog.buddieshr.com/create-a-culture-of-recognition-at-work-in-2024/"
                          >
                            Here is a complete guide on how to create a culture
                            of recognition in 2024
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </PageWrapper>
              </div>
            </div>
          </div>
        </main>
        <Footer refSource={refSource} />
      </div>
    </>
  );
}
